import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  de: {
    common: {
      // Navigation
      home: 'Startseite',
      login: 'Anmelden',
      register: 'Registrieren',
      logout: 'Abmelden',
      profile: 'Profil',
      admin: 'Admin',
      
      // Actions
      submit: 'Absenden',
      cancel: 'Abbrechen',
      save: 'Speichern',
      edit: 'Bearbeiten',
      delete: 'Löschen',
      search: 'Suchen',
      filter: 'Filtern',
      loading: 'Laden...',
      
      // Messages
      success: 'Erfolgreich',
      error: 'Fehler',
      warning: 'Warnung',
      info: 'Information',
    },
    auth: {
      // Login
      loginTitle: 'Anmelden',
      loginSubtitle: 'Melden Sie sich bei Ihrem Konto an',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      forgotPassword: 'Passwort vergessen?',
      noAccount: 'Noch kein Konto?',
      registerHere: 'Hier registrieren',
      
      // Register
      registerTitle: 'Registrieren',
      registerSubtitle: 'Erstellen Sie Ihr Versandkonto',
      firstName: 'Vorname',
      lastName: 'Nachname',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      zipCode: 'Postleitzahl',
      city: 'Stadt',
      country: 'Land',
      sponsorName: 'Name des Sponsors',
      mpgRegistrationDate: 'MPG-Registrierungsdatum',
      captcha: 'Bitte bestätigen Sie, dass Sie kein Roboter sind',
      alreadyHaveAccount: 'Sie haben bereits ein Konto?',
      loginHere: 'Hier anmelden',
      
      // Password
      currentPassword: 'Aktuelles Passwort',
      newPassword: 'Neues Passwort',
      confirmPassword: 'Passwort bestätigen',
      changePassword: 'Passwort ändern',
      resetPassword: 'Passwort zurücksetzen',
      
      // Messages
      loginSuccess: 'Erfolgreich angemeldet',
      registerSuccess: 'Registrierung erfolgreich! Prüfen Sie Ihre E-Mails für Ihr Passwort.',
      passwordChanged: 'Passwort erfolgreich geändert',
      resetEmailSent: 'Zurücksetzungs-E-Mail gesendet',
      invalidCredentials: 'Ungültige E-Mail oder Passwort',
    },
    address: {
      // Address fields
      address: 'Adresse',
      shippingAddress: 'Versandadresse',
      updateAddress: 'Adresse aktualisieren',
      currentAddress: 'Aktuelle Adresse',
      newAddress: 'Neue Adresse',
      
      // Address form
      enterAddress: 'Adresse eingeben',
      searchAddress: 'Adresse suchen',
      addressSuggestions: 'Adressvorschläge',
      selectAddress: 'Adresse auswählen',
      confirmAddress: 'Adresse bestätigen',
      
      // Messages
      addressUpdated: 'Adresse erfolgreich aktualisiert',
      emailNotification: 'Eine Bestätigungs-E-Mail wurde gesendet',
    },
    admin: {
      // Dashboard
      dashboard: 'Admin-Dashboard',
      users: 'Benutzer',
      userManagement: 'Benutzerverwaltung',
      addressLogs: 'Adressänderungsprotokolle',
      emailQueue: 'E-Mail-Warteschlange',
      
      // User list
      totalUsers: 'Benutzer gesamt',
      searchUsers: 'Benutzer suchen',
      filterByCountry: 'Nach Land filtern',
      filterByCity: 'Nach Stadt filtern',
      
      // User details
      userDetails: 'Benutzerdetails',
      registrationDate: 'Registrierungsdatum',
      lastUpdated: 'Zuletzt aktualisiert',
      viewLogs: 'Protokolle anzeigen',
      
      // Address logs
      changedBy: 'Geändert von',
      changedAt: 'Geändert am',
      changes: 'Änderungen',
      oldValue: 'Alter Wert',
      newValue: 'Neuer Wert',
      noLogs: 'Keine Protokolle verfügbar',
      
      // Email queue
      pendingEmails: 'Ausstehende E-Mails',
      recipient: 'Empfänger',
      subject: 'Betreff',
      createdAt: 'Erstellt am',
      preview: 'Vorschau',
      smtpNotConfigured: 'SMTP noch nicht konfiguriert. E-Mails werden in der Warteschlange gespeichert.',
    },
  },
  en: {
    common: {
      // Navigation
      home: 'Home',
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      profile: 'Profile',
      admin: 'Admin',
      
      // Actions
      submit: 'Submit',
      cancel: 'Cancel',
      save: 'Save',
      edit: 'Edit',
      delete: 'Delete',
      search: 'Search',
      filter: 'Filter',
      loading: 'Loading...',
      
      // Messages
      success: 'Success',
      error: 'Error',
      warning: 'Warning',
      info: 'Information',
    },
    auth: {
      // Login
      loginTitle: 'Login',
      loginSubtitle: 'Sign in to your account',
      email: 'Email Address',
      password: 'Password',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      registerHere: 'Register here',
      
      // Register
      registerTitle: 'Register',
      registerSubtitle: 'Create your shipping account',
      firstName: 'First Name',
      lastName: 'Last Name',
      street: 'Street',
      houseNumber: 'House Number',
      zipCode: 'ZIP Code',
      city: 'City',
      country: 'Country',
      sponsorName: 'Sponsor Name',
      mpgRegistrationDate: 'MPG Registration Date',
      captcha: 'Please confirm you are not a robot',
      alreadyHaveAccount: 'Already have an account?',
      loginHere: 'Login here',
      
      // Password
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      changePassword: 'Change Password',
      resetPassword: 'Reset Password',
      
      // Messages
      loginSuccess: 'Successfully logged in',
      registerSuccess: 'Registration successful! Check your email for your password.',
      passwordChanged: 'Password changed successfully',
      resetEmailSent: 'Reset email sent',
      invalidCredentials: 'Invalid email or password',
    },
    address: {
      // Address fields
      address: 'Address',
      shippingAddress: 'Shipping Address',
      updateAddress: 'Update Address',
      currentAddress: 'Current Address',
      newAddress: 'New Address',
      
      // Address form
      enterAddress: 'Enter Address',
      searchAddress: 'Search Address',
      addressSuggestions: 'Address Suggestions',
      selectAddress: 'Select Address',
      confirmAddress: 'Confirm Address',
      
      // Messages
      addressUpdated: 'Address updated successfully',
      emailNotification: 'A confirmation email has been sent',
    },
    admin: {
      // Dashboard
      dashboard: 'Admin Dashboard',
      users: 'Users',
      userManagement: 'User Management',
      addressLogs: 'Address Change Logs',
      emailQueue: 'Email Queue',
      
      // User list
      totalUsers: 'Total Users',
      searchUsers: 'Search users',
      filterByCountry: 'Filter by country',
      filterByCity: 'Filter by city',
      
      // User details
      userDetails: 'User Details',
      registrationDate: 'Registration Date',
      lastUpdated: 'Last Updated',
      viewLogs: 'View Logs',
      
      // Address logs
      changedBy: 'Changed By',
      changedAt: 'Changed At',
      changes: 'Changes',
      oldValue: 'Old Value',
      newValue: 'New Value',
      noLogs: 'No logs available',
      
      // Email queue
      pendingEmails: 'Pending Emails',
      recipient: 'Recipient',
      subject: 'Subject',
      createdAt: 'Created At',
      preview: 'Preview',
      smtpNotConfigured: 'SMTP not configured yet. Emails are queued.',
    },
  },
};

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'de',
    defaultNS: 'common',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
  });

export default i18n;
